import { useState, useCallback, useRef, useEffect } from 'react';
import { SyncPresenceConfig, syncPresenceRegular, syncPresenceCustom } from '../utilites/syncPresenceStatuses';
import { StatusPresentation } from '../components/ExPresence';

type PresenceCategory = "AVL" | "BSY" | "DND" | "INV";

interface PresenceChangeConfig {
  apiConfig: SyncPresenceConfig;
  user: any;
}

interface RegularPresenceChange {
  type: 'regular';
  category: PresenceCategory;
  duration?: number;
  dndOverride?: string;
}

interface CustomPresenceChange {
  type: 'custom';
  category: PresenceCategory;
  table: any;
  customStatus: string;
  dndOverride?: string;
}

type PresenceChange = RegularPresenceChange | CustomPresenceChange;

const TIMEOUT_DURATION = 5000; // 5 seconds

// TESTING FLAG: Set to true to simulate timeout scenarios (block prop updates)
// Set to false for normal operation
const TEST_TIMEOUT_MODE = false;

// TESTING: Use shorter timeout for easier testing (set to true for 2-second timeout)
const USE_SHORT_TIMEOUT = false;
const SHORT_TIMEOUT_DURATION = 2000; // 2 seconds for testing

const usePresenceManager = (config: PresenceChangeConfig) => {
  const [statusPresentation, setStatusPresentation] = useState<StatusPresentation>({ 
    kind: "InStatus", 
    status: "" 
  });
  
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expectedStatusRef = useRef<string | null>(null);
  const isProcessingRef = useRef<boolean>(false);

  // Clear timeout when component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Monitor user.display changes to detect if the expected status arrived
  useEffect(() => {
    if (statusPresentation.kind === "Pending" && expectedStatusRef.current) {
      const currentDisplay = config.user.display;
      const expectedStatus = expectedStatusRef.current;
      
      // In test mode, we ignore prop updates to simulate timeout scenarios
      if (TEST_TIMEOUT_MODE) {
        console.log('TEST MODE: Ignoring prop update to simulate timeout:', currentDisplay);
        return;
      }
      
      // Check if the user's display now matches what we expected
      if (currentDisplay && currentDisplay.includes(expectedStatus)) {
        // Success! The prop change arrived
        console.log('Presence change confirmed via prop update:', currentDisplay);
        setStatusPresentation({ kind: "InStatus", status: expectedStatus });
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        expectedStatusRef.current = null;
        isProcessingRef.current = false;
      }
    }
  }, [config.user.display, statusPresentation.kind]);

  const executePresenceChange = useCallback(async (change: PresenceChange) => {
    if (isProcessingRef.current) {
      console.warn('Presence change already in progress, ignoring request');
      return;
    }

    isProcessingRef.current = true;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    try {
      // Determine the expected status display text
      let expectedStatus: string;
      if (change.type === 'regular') {
        const statusMap = {
          AVL: "Available",
          BSY: "Busy", 
          DND: "Do not disturb",
          INV: "Invisible"
        };
        expectedStatus = statusMap[change.category];
      } else {
        // For custom statuses, use the custom text or table status
        expectedStatus = change.table.status === "[custom]" 
          ? change.customStatus 
          : change.table.status;
      }

      expectedStatusRef.current = expectedStatus;

      // Step 1: Set to Syncing
      setStatusPresentation({ kind: "Syncing", status: expectedStatus });

      // Step 2: Execute the sync call
      let result;
      if (change.type === 'regular') {
        result = await syncPresenceRegular(
          config.apiConfig,
          change.category,
          config.user,
          change.duration,
          change.dndOverride
        );
      } else {
        result = await syncPresenceCustom(
          config.apiConfig,
          change.category,
          config.user,
          change.table,
          change.customStatus,
          change.dndOverride
        );
      }

      if (result.success) {
        // Step 3: Set to Pending and start the timeout
        setStatusPresentation({ kind: "Pending", status: expectedStatus });
        
        const actualTimeoutDuration = USE_SHORT_TIMEOUT ? SHORT_TIMEOUT_DURATION : TIMEOUT_DURATION;
        console.log(`Starting ${actualTimeoutDuration}ms timeout for status: ${expectedStatus}`);
        if (TEST_TIMEOUT_MODE) {
          console.log('TEST MODE: Timeout will trigger (prop updates are blocked)');
        }
        
        // Set up the timeout
        timeoutRef.current = setTimeout(() => {
          console.log('Presence change timeout reached, forcing status update');
          setStatusPresentation({ kind: "InStatus", status: expectedStatus });
          expectedStatusRef.current = null;
          isProcessingRef.current = false;
          timeoutRef.current = null;
        }, actualTimeoutDuration);

      } else {
        // Handle failure
        console.error('Presence sync failed:', result.error);
        setStatusPresentation({ 
          kind: "Error", 
          status: expectedStatus,
          message: result.error || "Sync failed" 
        });
        expectedStatusRef.current = null;
        isProcessingRef.current = false;
      }

    } catch (error) {
      console.error('Unexpected error during presence change:', error);
      setStatusPresentation({ 
        kind: "Error", 
        status: expectedStatusRef.current || "Unknown",
        message: error instanceof Error ? error.message : "Unknown error" 
      });
      expectedStatusRef.current = null;
      isProcessingRef.current = false;
    }
  }, [config.apiConfig, config.user]);

  const changeRegularPresence = useCallback((
    category: PresenceCategory,
    duration?: number,
    dndOverride?: string
  ) => {
    executePresenceChange({
      type: 'regular',
      category,
      duration,
      dndOverride
    });
  }, [executePresenceChange]);

  const changeCustomPresence = useCallback((
    category: PresenceCategory,
    table: any,
    customStatus: string,
    dndOverride?: string
  ) => {
    executePresenceChange({
      type: 'custom',
      category,
      table,
      customStatus,
      dndOverride
    });
  }, [executePresenceChange]);

  // Expose testing functions to global scope for easy testing
  if (typeof window !== 'undefined') {
    (window as any).testPresenceTimeout = {
      enableTestMode: () => {
        console.log('TEST MODE: To enable test mode, set TEST_TIMEOUT_MODE = true in usePresenceManager.ts');
        console.log('TEST MODE: To use shorter timeout, set USE_SHORT_TIMEOUT = true in usePresenceManager.ts');
      },
      getCurrentStatus: () => statusPresentation,
      getExpectedStatus: () => expectedStatusRef.current,
      isProcessing: () => isProcessingRef.current
    };
  }

  return {
    statusPresentation,
    changeRegularPresence,
    changeCustomPresence,
    isProcessing: isProcessingRef.current
  };
};

export default usePresenceManager; 